/**
 * Design tokens — the single source of truth for colour, type, spacing and shape.
 *
 * Use through the MUI theme where possible (`sx={{ color: "primary.main" }}`,
 * `theme.palette.*`). Import these constants directly only in code that runs
 * outside a ThemeProvider or builds raw CSS strings (e.g. the grid components).
 */

// Brand — teal + navy, carried over from the original NPG WFM navigation.
export const brand = {
  teal: "#3796A6", // original brand teal (logo bar)
  tealDark: "#2A7D8E", // primary actions — AA contrast on white text
  tealDeep: "#1E6574", // hover / pressed
  tealSoft: "#E3F1F4", // selected rows, active menu items
  tealTint: "#F3F9FA", // subtle surfaces, table header
  navy: "#112233", // navigation bar
  navy2: "#1A2F3F",
};

// Neutral slate scale (text, borders, surfaces).
export const neutral = {
  0: "#FFFFFF",
  25: "#FBFCFD",
  50: "#F6F8FA",
  100: "#EEF2F5",
  200: "#E2E8EE",
  300: "#CBD5DF",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
};

export const semantic = {
  success: "#16A34A",
  warning: "#D97706",
  error: "#DC2626",
  info: "#0284C7",
};

export const fontFamily =
  '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const monoFamily = 'ui-monospace, "Cascadia Mono", Consolas, monospace';

// Compact enterprise type scale (px).
export const fontSize = {
  xs: 11, // table cells, chips, captions
  sm: 12, // buttons, inputs, secondary text
  md: 13, // body
  lg: 15, // section titles
  xl: 18, // page titles
};

export const radius = { sm: 4, md: 6, lg: 8, pill: 999 };

export const shadow = {
  none: "none",
  xs: "0 1px 2px rgba(15, 23, 42, 0.06)",
  sm: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
  md: "0 4px 12px rgba(15, 23, 42, 0.08)",
  lg: "0 12px 32px rgba(15, 23, 42, 0.12)",
};

// Fixed chrome heights (px). NOTE: the navigation bars are now 30px each (layout/HorizontalNav.jsx);
// these two values are no longer read anywhere.
export const layout = {
  topBarHeight: 36,
  navBarHeight: 34,
  pagePadding: 8,
};

/*
 * ORIGINAL look (the user's chosen style): emerald/mint grids and section headers,
 * blue primary + orange secondary buttons, blue links, mint job strip.
 * Change these values to re-skin the whole app.
 */
export const original = {
  mint: "#e8f5f1", // table & section headers
  mintStrong: "#C0F0EA", // job summary strip
  mintBorder: "#b2d8ce",
  emerald: "#059669", // accents in grids
  emeraldDark: "#047857",
  emeraldBottom: "#0a9e7a", // header bottom rule
  headerText: "#0a3328",
  headerMuted: "#4d8578",
  stripe: "#ffffff", // alternate-row colour — white = one colour for all rows (was mint "#f2f9f6")
  hover: "#e6f4f0",
  surface: "#f7faf9",
  border: "#ddeee9",
  borderMid: "#bcd9d2",
  text: "#1a3530",
  textMono: "#1e5f50",
  textMuted: "#6fa99b",
  accentLight: "#d1fae5",
  link: "#1976d2",
  primaryBtn: "#2979ff",
  primaryBtnHover: "#1565c0",
  secondaryBtn: "#FFAF38",
  secondaryBtnHover: "#f59e0b",
  secondaryBtnText: "rgba(52,49,76,1)",
};

// Table density & colours shared by every grid.
export const table = {
  rowHeight: 28,
  headerHeight: 32,
  headerBg: original.mint,
  headerText: original.headerText,
  headerBorder: original.mintBorder,
  stripe: original.stripe,
  hover: original.hover,
  selected: original.accentLight,
  border: original.border,
};

// Tinted bars that separate sections (page strips, card title bars, section headers).
export const sectionHeader = {
  bg: original.mint,
  strip: original.mintStrong,
  border: original.mintBorder,
  accent: original.emeraldBottom,
  text: original.headerText,
};

// Palette for the two custom grids (ServerDataTable / DataTable). Keys match the
// grids' `T` object; values are the original grid colours.
export const gridTokens = {
  bg: "#ffffff",
  surface: original.surface,
  stripe: original.stripe,
  hover: original.hover,
  hdrBg: original.mint,
  hdrBorder: original.mintBorder,
  hdrText: original.headerText,
  hdrMuted: original.headerMuted,
  hdrBottom: original.emeraldBottom,
  accent: original.emerald,
  accentHov: original.emeraldDark,
  accentLight: original.accentLight,
  border: original.border,
  borderMid: original.borderMid,
  text: original.text,
  textMono: original.textMono,
  textMuted: original.textMuted,
  green: "#059669",
  red: "#dc2626",
  orange: "#d97706",
  blue: "#0369a1",
  font: fontFamily,
  rowH: "26px",
  hdrH: "30px",
};

const tokens = { brand, neutral, semantic, original, fontFamily, monoFamily, fontSize, radius, shadow, layout, table, sectionHeader };
export default tokens;
